<template>
  <div v-bind="$attrs" ref="hostElement" class="content-selector-container h-full" />
  <NModal class="h-full w-full content-selector" show :to="attachTo ?? hostElement ?? undefined" :z-index="zIndex">
    <NCard :bordered="false" class="content-selector__card w-full h-full pa-4" :content-style="{ overflowY: 'auto' }">
      <template #header>
        <Navbar />
      </template>
      <NGrid class="h-full overflow-hidden" cols="6" item-responsive :x-gap="isMultimediaSelectorType ? 8 : 0">
        <NGridItem class="overflow-auto" :span="isMultimediaSelectorType ? '4' : '6'">
          <CFullScreenLoader v-if="isRestoringSelection" fit-parent />
          <Content v-else-if="activeSelectorType === 'content'" />
          <Assets v-else-if="activeSelectorType === 'assets'" />
          <ExternalLinks v-else-if="activeSelectorType === 'external_links'" @add="onExternalLinkAdd" />
          <AiImages v-else-if="activeSelectorType === 'ai_images'" @add="onAiImageAdd" />
          <PexelsImages v-else-if="activeSelectorType === 'pexels_images'" @add="onPexelsImageAdd" />
          <ThemeImages v-else-if="activeSelectorType === 'theme'" />
          <MultimediaSelectorApp
            v-else-if="isMultimediaSelectorType"
            :app-id="activeSelectorType.slice('multimedia-selector-'.length)"
            @add="onMultimediaSelectorAppAdd"
          />
        </NGridItem>
        <NGridItem v-if="isMultimediaSelectorType" span="2">
          <SelectionPanel />
        </NGridItem>
      </NGrid>
      <template #footer>
        <div class="content-selector__footer flex justify-end pt-3 gap-1">
          <CButton :disabled="isRestoringSelection" @click="emit('cancel')">
            {{ t('canvasUI.common.cancel') }}
          </CButton>
          <CButton
            :disabled="isPrimaryDisabled(isDragNDropEnabled ? addedItems : selectedItems) || isRestoringSelection"
            type="primary"
            @click="handleDone"
          >
            {{ primaryActionText ?? t('canvasUI.common.select') }}
          </CButton>
        </div>
      </template>
    </NCard>
  </NModal>
</template>

<script setup lang="ts">
import { NGridItem, NGrid, NModal, NCard } from 'naive-ui'
import { provide, toRefs, onMounted, onBeforeUnmount, ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { isEmpty, uniq } from 'lodash-es'

import Navbar from '@content-selector/components/Navbar.vue'
import SelectionPanel from '@content-selector/components/Content/SelectionPanel/SelectionPanel.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import Content from '@content-selector/components/Content.vue'
import Assets from '@content-selector/components/Assets.vue'
import ExternalLinks from '@content-selector/components/ExternalLinks.vue'
import AiImages from '@content-selector/components/AiImages.vue'
import PexelsImages from '@content-selector/components/PexelsImages.vue'
import ThemeImages from '@content-selector/components/ThemeImages.vue'
import MultimediaSelectorApp from '@content-selector/components/MultimediaSelectorApp.vue'
import CFullScreenLoader from '@lib/components/CFullScreenLoader/CFullScreenLoader.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import useAppEmit from '@lib/composables/appEmit.use'
import useConfirmation from '@lib/composables/useConfirmation'
import useAppStore, {
  type ContentSelectorSelectedItem,
  type SelectedAsset,
  type SelectedExternalLink,
  type SelectedFile,
  type SelectedPage,
} from '@content-selector/stores/app'
import useApiStore from '@content-selector/stores/api'
import { createPageId } from '@content-selector/util/file'
import type { AppProps } from '@content-selector/appTypes'
import type { FileRetrieve } from '@openapi'

const { t } = useI18n()
const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'primary-action', p: { selectedItems: ContentSelectorSelectedItem[] }): void
  (e: 'asset-deleted', id: string): void
}>()

useAppEmit(emit)
const props = withDefaults(defineProps<AppProps>(), { minSelections: 0 })
const hostElement = ref(null)

const store = useAppStore()
provide('contentSelectorStore', store)

const {
  getThumbnailUrl,
  addedItems,
  setItems,
  activeSelectorType,
  allowedContentTypes,
  setActiveSelectorType,
  setAllowedSelectorTypes,
  setAllowedIds,
  setState,
  selectItem,
  setAppName,
  setSearchMetadataFilters,
  setAllowedOriginalExtensions,
  setDisableNoSharable,
  setDisableNoPptxData,
  setSearchQuery,
  setSelectedSection,
  activeFolder,
  activeFile,
  selectedItems,
  isDragNDropEnabled,
  allowMultimediaSelectorApp,
} = store

const apiStore = useApiStore(store)
provide('contentSelectorApiStore', apiStore)
const confirmation = useConfirmation()

const {
  state: api,
  actions: { init: initApi },
} = apiStore

store.setApiStore(apiStore)

const themeVars = useThemeVars()
const isRestoringSelection = ref(false)
const isMultimediaSelectorType = computed(
  () => activeSelectorType.value.startsWith('multimedia-selector-') && props.allowMultimediaSelectorApp,
)
provide('appProps', toRefs(props))
setAppName(props.appName)
if (!isEmpty(props.initialMetadataFilters)) setSearchMetadataFilters(props.initialMetadataFilters)
if (props.allowedOriginalExtensions?.length) setAllowedOriginalExtensions(props.allowedOriginalExtensions)
if (props.allowedIds?.length) setAllowedIds(props.allowedIds)
if ('disableNoSharable' in props) setDisableNoSharable(props.disableNoSharable)
if ('disableNoPptxData' in props) setDisableNoPptxData(props.disableNoPptxData)
initApi(props)
allowMultimediaSelectorApp.value = props.allowMultimediaSelectorApp

function isPrimaryDisabled(addedItems: ContentSelectorSelectedItem[]) {
  const isNotInMinMaxRange =
    addedItems.length < props.minSelections ||
    (props.maxSelections !== undefined && addedItems.length > props.maxSelections)

  return isNotInMinMaxRange
}

function onExternalLinkAdd(item: { url: string; content_type: 'image' | 'video' }) {
  selectItem({
    id: `${item.url}-${Date.now()}`,
    url: item.url,
    thumbnail_url: item.content_type === 'image' ? item.url : undefined,
    type: 'external_link',
    content_type: item.content_type,
    from: 'custom',
  })
}
function onAiImageAdd(item: { url: string; content_type: 'image' | 'video' }) {
  selectItem({
    id: `${item.url}-${Date.now()}`,
    url: item.url,
    thumbnail_url: item.content_type === 'image' ? item.url : undefined,
    type: 'external_link',
    content_type: item.content_type,
    from: 'ai',
  })
}

function onPexelsImageAdd(item: { url: string; content_type: 'image' | 'video' }) {
  selectItem({
    id: `${item.url}-${Date.now()}`,
    url: item.url,
    thumbnail_url: item.content_type === 'image' ? item.url : undefined,
    type: 'external_link',
    content_type: item.content_type,
    from: 'pexels',
  })
}

function onMultimediaSelectorAppAdd(item: {
  id: string
  name: string
  thumbnailUrl: string
  url: string
  type: 'image' | 'video'
  source: string
}) {
  selectItem({
    id: item.id,
    url: item.url,
    thumbnail_url: item.type === 'image' ? item.thumbnailUrl : undefined,
    type: 'external_link',
    content_type: item.type,
    from: (['custom', 'pexels', 'unsplash', 'ai'].includes(item.source) ? item.source : 'custom') as
      | 'custom'
      | 'pexels'
      | 'unsplash'
      | 'ai',
  })
}

const handleDone = () => {
  if (!isDragNDropEnabled.value) {
    return emit('primary-action', {
      selectedItems: selectedItems.value,
    })
  }

  // check if someone forgot to add sections
  if (
    selectedItems.value.length > 0 &&
    selectedItems.value.some(
      (selectedSection) => !addedItems.value.some((addedItem) => addedItem.id === selectedSection.id),
    )
  ) {
    confirmation.warning({
      hasExit: false,
      title: t('canvasUI.contentSelector.leaveDialog.title'),
      content: t('canvasUI.contentSelector.leaveDialog.content'),
      confirmText: t('canvasUI.common.ok'),
      cancelText: t('canvasUI.common.cancel'),
      zIndex: 3000, // to work with collection player
      onConfirm: () =>
        emit('primary-action', {
          selectedItems: addedItems.value,
        }),
    })
  } else {
    emit('primary-action', {
      selectedItems: addedItems.value,
    })
  }
}

watch([activeFolder, activeFile], () => {
  if (isDragNDropEnabled.value) {
    selectedItems.value = []
  }
})

const DISABLE_IFRAME_EVENTS_CLASS = 'content-selector--disable-iframe-events'

// In Chrome, iframes underneath modal "steal" dragover event from elements over it
const disableIframeEvents = () => document.body.classList.add(DISABLE_IFRAME_EVENTS_CLASS)
const enableIframeEvents = () => document.body.classList.remove(DISABLE_IFRAME_EVENTS_CLASS)

onMounted(async () => {
  disableIframeEvents()
  setSearchQuery('')
  allowedContentTypes.value = props.allowedContentTypes ?? []
  setActiveSelectorType(props.allowedSelectorTypes?.[0])
  setAllowedSelectorTypes(props.allowedSelectorTypes ?? [])

  if (!props.selections?.length) {
    if (isDragNDropEnabled.value) {
      return setItems('added', [])
    } else {
      return setItems('selected', [])
    }
  }

  try {
    isRestoringSelection.value = true
    const fileIds = uniq(
      props.selections.map((s) => ('fileId' in s ? s.fileId : null)).filter((id): id is FileRetrieve['id'] => !!id),
    )
    const files = await Promise.allSettled(fileIds.map((id) => api.fileFetcher?.(id)))
      .then((res) => res.map((r) => r.status === 'fulfilled' && r.value))
      .then((res) => res.filter((r): r is FileRetrieve => !!r))
    const filesById = files.reduce((acc, f) => ({ ...acc, [f.id]: f }), {} as Record<FileRetrieve['id'], FileRetrieve>)
    const restoredItems: ContentSelectorSelectedItem[] = props.selections
      .map((s) => {
        if ('fileId' in s) {
          const file = filesById[s.fileId]
          if (!file) throw new Error(`file not found: ${s.fileId}`)
          if (s.type === 'page' && typeof s.pageIndex === 'number') {
            return {
              type: 'page',
              id: createPageId(s.fileId, s.pageIndex),
              file: { id: file.id, name: file.name },
              page_index: s.pageIndex,
              permissions: file.permissions,
              thumbnail_url: file.content_thumbnails?.[s.pageIndex],
            } as SelectedPage
          } else if (s.type === 'file') {
            return {
              type: 'file',
              id: s.fileId,
              thumbnail_url: file.thumbnail_url ?? '',
              created_by: file.created_by,
              tags: file.tags ?? [],
              name: file.name ?? '',
              status: file.status,
              modified_at: file.modified_at,
              content_type: file.content_type,
              expires_at: file.expires_at,
            } as SelectedFile
          } else if (s.type === 'asset') {
            return {
              type: 'asset',
              id: s.fileId,
              thumbnail_url: getThumbnailUrl(file),
              content_url: file.content_url,
              content_extension: file.content_extension,
              file: { id: file.id, name: file.name },
            } as SelectedAsset
          }
        } else if ('url' in s) {
          return {
            type: 'external_link',
            id: `${s.url}-${Date.now()}`,
            url: s.url,
            thumbnail_url: s.content_type === 'image' ? s.url : undefined,
            content_type: s.content_type,
            from: s.from || 'custom',
          } as SelectedExternalLink
        }
      })
      .filter((item): item is ContentSelectorSelectedItem => !!item)

    if (isDragNDropEnabled.value) {
      setItems('added', restoredItems, props.maxSelections)
    } else {
      setItems('selected', restoredItems, props.maxSelections)
    }
  } catch (e) {
    console.error(e)
  } finally {
    isRestoringSelection.value = false
  }
})

onBeforeUnmount(() => {
  enableIframeEvents()
  setSelectedSection('all')
  setSearchMetadataFilters({})
  setAllowedIds([])
  setState({ activeFolder: null, activeFile: null })
})
</script>

<style lang="scss" scoped>
.content-selector-container {
  font-family: Roboto, sans-serif; // stop any themes from altering our content selector

  :deep(iframe) {
    pointer-events: auto; // cancel effect of content-selector--disable-iframe-events
  }

  :deep(.n-modal-scroll-content) {
    height: 100%;
    padding: 24px;
  }
}

.content-selector__card {
  background-color: v-bind('themeVars.primary6');
}
</style>

<style>
.content-selector--disable-iframe-events iframe {
  pointer-events: none;
}
</style>
